import { useState } from 'react';
import { useFormik } from 'formik';
import getConfig from 'next/config';
import * as yup from 'yup';

import {
  useAppDispatch,
  useSeekerCCState,
  useSeekerState,
  useFlowState,
} from '@/components/AppState';
import { useSeekerAccountCreation } from '@/components/hooks/useSeekerAccountCreation';
import { validatePasswordForm } from '@/components/features/accountCreation/accountCreationForm';
import { VALIDATION_MESSAGES, ABBREVIATION_BY_SERVICE_TYPE, JOB_MFE_CC_PAJ } from '@/constants';
import userEmailStore from '@/lib/UserEmailStore';
import validationSchemaName from '@/utilities/validationSchemaName';
import { ServiceType } from '@/__generated__/globalTypes';

const {
  publicRuntimeConfig: { CZEN_GENERAL },
} = getConfig();

type SignUpFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

const useSignUpForm = () => {
  const { firstName, lastName, careKind } = useSeekerCCState();
  const { zipcode } = useSeekerState();
  const { referrerCookie } = useFlowState();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const schema = validationSchemaName.shape({
    email: yup.string().required(VALIDATION_MESSAGES.EMAIL_REQUIRED),
  });

  const setFormikSubmittingFalse = () => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    formik.setSubmitting(false);
  };

  const { executeSeekerAccountCreate } = useSeekerAccountCreation({
    vertical: ABBREVIATION_BY_SERVICE_TYPE[ServiceType.CHILD_CARE],
    nextPageURL: CZEN_GENERAL + JOB_MFE_CC_PAJ,
    setFormikSubmittingFalse,
    setErrorMessage,
  });

  const handleSubmit = (values: SignUpFormValues) => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    if (formik.status.zipError) {
      return;
    }

    dispatch({
      type: 'setShowChildDetails',
      showChildDetails: true,
    });

    userEmailStore.setEmail(values.email);

    executeSeekerAccountCreate({
      values: {
        ...values,
        phoneNumber: '',
        howDidYouHearAboutUs: '',
      },
      zipcode,
      referrerCookie,
      careKind,
      skipAccountCreation: false,
    });
  };

  const formik = useFormik<SignUpFormValues>({
    initialValues: {
      firstName: firstName || '',
      lastName: lastName || '',
      email: userEmailStore.getEmail() || '',
      password: '',
    },
    validationSchema: schema,
    validate: validatePasswordForm,
    onSubmit: handleSubmit,
  });

  return {
    errorMessage,
    formik,
  };
};

export default useSignUpForm;
